import {
  collection, doc, addDoc, deleteDoc, getDoc, getDocs, setDoc, updateDoc,
  onSnapshot, query, orderBy, limit, serverTimestamp, increment, writeBatch,
} from 'firebase/firestore';
import { db } from '../firebaseClient';
import { logPlatformEvent } from './eventHelpers';

// ── Path helpers ──
const liveClassesCol = (orgId) => collection(db, 'organizations', orgId, 'liveClasses');
const liveClassDoc = (orgId, classId) => doc(db, 'organizations', orgId, 'liveClasses', classId);

export function generateLocalId() {
  return doc(collection(db, '_')).id;
}

// ── Create / read / update ──

export async function createLiveClass(orgId, {
  title, description, courseId, moduleId, instructorUid, instructorName,
  scheduledAtMs, durationMinutes, capacity, settings,
}) {
  const classId = generateLocalId();
  await setDoc(liveClassDoc(orgId, classId), {
    title: title || 'Untitled Live Class',
    description: description || '',
    courseId: courseId || null,
    moduleId: moduleId || null,
    instructorUid,
    instructorName: instructorName || 'Instructor',
    scheduledAtMs: scheduledAtMs || null,
    durationMinutes: durationMinutes || 60,
    capacity: capacity || null,
    status: 'scheduled',
    settings: {
      recordingEnabled: settings?.recordingEnabled ?? true,
      chatEnabled: settings?.chatEnabled ?? true,
      qnaEnabled: settings?.qnaEnabled ?? true,
      autoAttendance: settings?.autoAttendance ?? true,
    },
        agoraChannelName: classId,
    startedAtMs: null,
    endedAtMs: null,
    attendeeCount: 0,
    createdAt: serverTimestamp(),
  });
  logPlatformEvent({
    type: 'LIVE_CLASS_SCHEDULED',
    actorUid: instructorUid,
    orgId,
    targetPath: `organizations/${orgId}/liveClasses/${classId}`,
    metadata: { title: title || 'Untitled Live Class', scheduledAtMs: scheduledAtMs || null },
  });
  return { classId };
}

export async function getLiveClass(orgId, classId) {
  const snap = await getDoc(liveClassDoc(orgId, classId));
  if (!snap.exists()) return { liveClass: null };
  return { liveClass: { id: snap.id, ...snap.data() } };
}

export async function updateLiveClass(orgId, classId, updates) {
  await updateDoc(liveClassDoc(orgId, classId), updates);
}

export async function deleteLiveClass(orgId, classId) {
  await deleteDoc(liveClassDoc(orgId, classId));
}

// Subscribes to every live class for a course (or the whole org if
// courseId is omitted), ordered by scheduled time — for listing UIs.
export function subscribeLiveClasses(orgId, cb) {
  const q = query(liveClassesCol(orgId), orderBy('scheduledAtMs', 'asc'));
  return onSnapshot(q, (snap) => {
    cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

export function subscribeLiveClass(orgId, classId, cb) {
  return onSnapshot(liveClassDoc(orgId, classId), (snap) => {
    cb(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  });
}

// ── Session lifecycle ──

async function clearSubcollection(orgId, classId, subcollection) {
  const snap = await getDocs(collection(db, 'organizations', orgId, 'liveClasses', classId, subcollection));
  if (snap.empty) return;
  const batch = writeBatch(db);
  snap.docs.forEach((d) => batch.delete(d.ref));
  await batch.commit();
}

export async function startLiveClass(orgId, classId) {
  await Promise.all([
    clearSubcollection(orgId, classId, 'attendees'),
    clearSubcollection(orgId, classId, 'chatMessages'),
    clearSubcollection(orgId, classId, 'reactions'),
  ]);
  await updateDoc(liveClassDoc(orgId, classId), {
    status: 'live',
    startedAtMs: Date.now(),
    attendeeCount: 0,
  });
}

export async function endLiveClass(orgId, classId) {
  await updateDoc(liveClassDoc(orgId, classId), {
    status: 'ended',
    endedAtMs: Date.now(),
  });
  logPlatformEvent({
    type: 'LIVE_CLASS_ENDED',
    orgId,
    targetPath: `organizations/${orgId}/liveClasses/${classId}`,
    metadata: {},
  });
}

// ── Attendance ──

export async function joinLiveClass(orgId, classId, uid, name, dp) {
  const banSnap = await getDoc(doc(db, 'organizations', orgId, 'liveClasses', classId, 'banned', uid));
  if (banSnap.exists()) throw new Error('BANNED');

    await setDoc(doc(db, 'organizations', orgId, 'liveClasses', classId, 'attendees', uid), {
    name, dp: dp || '', joinedAtMs: Date.now(), leftAtMs: null,
  });
}

export async function leaveLiveClass(orgId, classId, uid) {
  const ref = doc(db, 'organizations', orgId, 'liveClasses', classId, 'attendees', uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
    await updateDoc(ref, { leftAtMs: Date.now() });
}

export function subscribeAttendees(orgId, classId, cb) {
  const q = query(collection(db, 'organizations', orgId, 'liveClasses', classId, 'attendees'), orderBy('joinedAtMs', 'asc'));
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ uid: d.id, ...d.data() }))));
}

export function subscribeSelfAttendeeStatus(orgId, classId, uid, cb) {
  return onSnapshot(doc(db, 'organizations', orgId, 'liveClasses', classId, 'attendees', uid), (snap) => cb(snap.exists()));
}

export async function kickAttendee(orgId, classId, uid) {
  await leaveLiveClass(orgId, classId, uid);
}

export async function banAttendee(orgId, classId, uid) {
  await setDoc(doc(db, 'organizations', orgId, 'liveClasses', classId, 'banned', uid), { bannedAt: serverTimestamp() });
  await leaveLiveClass(orgId, classId, uid);
}

// ── Chat ──

export function subscribeChatMessages(orgId, classId, cb) {
  const q = query(
    collection(db, 'organizations', orgId, 'liveClasses', classId, 'chatMessages'),
    orderBy('createdAt', 'asc'), limit(300)
  );
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
}

export async function sendChatMessage(orgId, classId, uid, name, dp, text) {
  const trimmed = text?.trim();
  if (!trimmed) return;
  await addDoc(collection(db, 'organizations', orgId, 'liveClasses', classId, 'chatMessages'), {
    uid, name, dp: dp || '', text: trimmed, createdAt: serverTimestamp(),
  });
}

// ── Reactions ──

export function subscribeReactions(orgId, classId, cb) {
  const q = query(
    collection(db, 'organizations', orgId, 'liveClasses', classId, 'reactions'),
    orderBy('createdAt', 'desc'), limit(20)
  );
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
}

export async function sendReaction(orgId, classId, uid, emoji) {
  await addDoc(collection(db, 'organizations', orgId, 'liveClasses', classId, 'reactions'), {
    uid, emoji, createdAt: serverTimestamp(),
  });
}

// ── Q&A ──

export function subscribeQuestions(orgId, classId, cb) {
  const q = query(
    collection(db, 'organizations', orgId, 'liveClasses', classId, 'questions'),
    orderBy('createdAt', 'asc')
  );
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
}

export async function askQuestion(orgId, classId, uid, name, text) {
  const trimmed = text?.trim();
  if (!trimmed) return;
  await addDoc(collection(db, 'organizations', orgId, 'liveClasses', classId, 'questions'), {
    uid, name, text: trimmed, upvotes: 0, answered: false, createdAt: serverTimestamp(),
  });
}

export async function markQuestionAnswered(orgId, classId, questionId) {
  await updateDoc(doc(db, 'organizations', orgId, 'liveClasses', classId, 'questions', questionId), {
    answered: true,
  });
}

// ── Polls ──

const pollsCol = (orgId, classId) => collection(db, 'organizations', orgId, 'liveClasses', classId, 'polls');
const pollDoc = (orgId, classId, pollId) => doc(db, 'organizations', orgId, 'liveClasses', classId, 'polls', pollId);

export async function createPoll(orgId, classId, { question, options }) {
  const pollId = generateLocalId();
  await setDoc(pollDoc(orgId, classId, pollId), {
    question: question || 'Untitled Poll',
    options: (options || []).map((text, i) => ({ id: `opt${i}`, text })),
    status: 'open',
    createdAt: serverTimestamp(),
    closedAt: null,
  });
  return { pollId };
}

export async function closePoll(orgId, classId, pollId) {
  await updateDoc(pollDoc(orgId, classId, pollId), { status: 'closed', closedAt: Date.now() });
}

// Subscribes to whichever poll is most recent for this class — the host
// closes one before launching the next, so there's only ever one "active"
// poll. This also means a learner who joins mid-results still sees the
// last poll's tally instead of nothing.
export function subscribeActivePoll(orgId, classId, cb) {
  const q = query(pollsCol(orgId, classId), orderBy('createdAt', 'desc'), limit(1));
  return onSnapshot(q, (snap) => {
    if (snap.empty) { cb(null); return; }
    const d = snap.docs[0];
    cb({ id: d.id, ...d.data() });
  });
}

export async function submitPollVote(orgId, classId, pollId, uid, optionId) {
  await setDoc(doc(db, 'organizations', orgId, 'liveClasses', classId, 'polls', pollId, 'votes', uid), {
    optionId, votedAt: Date.now(),
  });
}

export function subscribePollVotes(orgId, classId, pollId, cb) {
  return onSnapshot(collection(db, 'organizations', orgId, 'liveClasses', classId, 'polls', pollId, 'votes'), (snap) => {
    cb(snap.docs.map((d) => ({ uid: d.id, ...d.data() })));
  });
}

// ── Live Quiz ──
// Same "one active item at a time" shape as Polls, plus a correctOptionId
// so learners get right/wrong feedback instead of just a distribution.

const liveQuizzesCol = (orgId, classId) => collection(db, 'organizations', orgId, 'liveClasses', classId, 'liveQuizzes');
const liveQuizDoc = (orgId, classId, quizId) => doc(db, 'organizations', orgId, 'liveClasses', classId, 'liveQuizzes', quizId);

export async function createLiveQuiz(orgId, classId, { question, options, correctOptionId }) {
  const quizId = generateLocalId();
  await setDoc(liveQuizDoc(orgId, classId, quizId), {
    question: question || 'Untitled Question',
    options: (options || []).map((text, i) => ({ id: `opt${i}`, text })),
    correctOptionId,
    status: 'open',
    createdAt: serverTimestamp(),
    closedAt: null,
  });
  return { quizId };
}

export async function closeLiveQuiz(orgId, classId, quizId) {
  await updateDoc(liveQuizDoc(orgId, classId, quizId), { status: 'closed', closedAt: Date.now() });
}

export function subscribeActiveLiveQuiz(orgId, classId, cb) {
  const q = query(liveQuizzesCol(orgId, classId), orderBy('createdAt', 'desc'), limit(1));
  return onSnapshot(q, (snap) => {
    if (snap.empty) { cb(null); return; }
    const d = snap.docs[0];
    cb({ id: d.id, ...d.data() });
  });
}

export async function submitQuizAnswer(orgId, classId, quizId, uid, optionId, correctOptionId) {
  await setDoc(doc(db, 'organizations', orgId, 'liveClasses', classId, 'liveQuizzes', quizId, 'answers', uid), {
    optionId,
    correct: optionId === correctOptionId,
    answeredAt: Date.now(),
  });
}

export function subscribeQuizAnswers(orgId, classId, quizId, cb) {
  return onSnapshot(collection(db, 'organizations', orgId, 'liveClasses', classId, 'liveQuizzes', quizId, 'answers'), (snap) => {
    cb(snap.docs.map((d) => ({ uid: d.id, ...d.data() })));
  });
}

// ── Whiteboard visibility ──
// Just an open/closed flag. The actual stroke data flows over Agora's RTC
// data channel, not Firestore — high-frequency draw events don't need to
// be queryable or persisted, just fast.

const whiteboardDoc = (orgId, classId) => doc(db, 'organizations', orgId, 'liveClasses', classId, 'whiteboard', 'state');

export async function setWhiteboardOpen(orgId, classId, open) {
  await setDoc(whiteboardDoc(orgId, classId), { open }, { merge: true });
}

export function subscribeWhiteboardOpen(orgId, classId, cb) {
  return onSnapshot(whiteboardDoc(orgId, classId), (snap) => cb(!!snap.data()?.open));
}